import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Clock, Save, RotateCcw } from 'lucide-react';
import { notesApi } from '@/lib/notesApi';
import { extractError } from '@/lib/errors';
import { TagPortalHolidays } from './TagPortalHolidays';

const DAYS = [['mon', 'Monday'], ['tue', 'Tuesday'], ['wed', 'Wednesday'], ['thu', 'Thursday'], ['fri', 'Friday'], ['sat', 'Saturday'], ['sun', 'Sunday']];
const TIME = 'h-9 w-[7.5rem] rounded-lg border border-input bg-background px-2 text-sm text-foreground disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]';

function Switch({ on, onChange, testid, label }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} onClick={() => onChange(!on)} data-testid={testid}
      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors duration-200 ${on ? 'bg-emerald-600' : 'bg-muted-foreground/30'}`}>
      <span className={`absolute left-0 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${on ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
    </button>
  );
}

function DayRow({ id, label, d, onChange }) {
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-border py-2.5 last:border-0" data-testid={`tagws-hours-day-${id}`}>
      <Switch on={d.open} onChange={(open) => onChange({ ...d, open })} testid={`tagws-hours-open-${id}`} label={`${label} open`} />
      <span className="w-24 text-sm font-medium text-foreground">{label}</span>
      {d.open ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <input type="time" value={d.start} onChange={(e) => onChange({ ...d, start: e.target.value })} className={TIME} data-testid={`tagws-hours-start-${id}`} />
          to
          <input type="time" value={d.end} onChange={(e) => onChange({ ...d, end: e.target.value })} className={TIME} data-testid={`tagws-hours-end-${id}`} />
        </div>
      ) : <span className="text-xs font-medium text-red-600">Closed all day</span>}
    </div>
  );
}

// Portal opening hours (India time). Outside them the portal shows the maintenance page.
export function TagPortalHours() {
  const [h, setH] = useState(null);
  const [lock, setLock] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => {
    notesApi.get('/notes/tag-workspace/portal-hours').then(({ data }) => setH(data)).catch((e) => toast.error(extractError(e, 'Could not load the portal hours.')));
    notesApi.get('/notes/tag-workspace/portal-lock').then(({ data }) => setLock(data)).catch(() => {});
  };
  useEffect(load, []);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await notesApi.put('/notes/tag-workspace/portal-hours', { enabled: h.enabled, days: h.days });
      setH(data);
      toast.success(data.enabled ? 'Portal hours saved.' : 'Portal hours turned off.');
      notesApi.get('/notes/tag-workspace/portal-lock').then(({ data: l }) => setLock(l)).catch(() => {});
    } catch (e) { toast.error(extractError(e, 'Could not save the portal hours.')); } finally { setBusy(false); }
  };
  const follow = async () => {
    try {
      await notesApi.post('/notes/tag-workspace/portal-lock/follow-hours');
      window.dispatchEvent(new Event('tagws-locked'));
      load();
      toast.success('The portal follows its hours again.');
    } catch (e) { toast.error(extractError(e, 'Could not change the portal lock.')); }
  };
  if (!h) return <div className="py-10 text-center"><Loader2 size={18} className="mx-auto animate-spin text-muted-foreground" /></div>;
  return (
    <div className="space-y-4" data-testid="tagws-portal-hours">
      <div className="flex items-start justify-between gap-3 rounded-xl border border-border p-4">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground"><Clock size={15} /> Lock the portal outside these hours</div>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">India time (IST), now {h.now}. Outside the hours everyone sees the maintenance page and the portal reopens on its own. Typing the phrase still unlocks it any time.</p>
        </div>
        <Switch on={h.enabled} onChange={(enabled) => setH({ ...h, enabled })} testid="tagws-hours-enabled" label="Use portal hours" />
      </div>
      {h.holiday_today ? <p className="rounded-xl border border-red-600/30 bg-red-600/10 p-3 text-xs text-foreground" data-testid="tagws-holiday-today">Today is a holiday ({h.holiday_today}), so the portal is locked all day.</p> : null}
      {lock?.override ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3.5" data-testid="tagws-hours-override">
          <p className="text-xs text-foreground">Unlocked with the phrase during off hours or a holiday, so the portal stays open until you lock it again.</p>
          <button type="button" onClick={follow} className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-border bg-background px-3 text-xs font-semibold hover:bg-secondary" data-testid="tagws-hours-follow"><RotateCcw size={13} /> Follow the hours again</button>
        </div>
      ) : null}
      <div className={`rounded-xl border border-border px-4 transition-opacity ${h.enabled ? '' : 'opacity-50'}`}>
        {DAYS.map(([id, label]) => <DayRow key={id} id={id} label={label} d={h.days[id]} onChange={(d) => setH({ ...h, days: { ...h.days, [id]: d } })} />)}
      </div>
      <TagPortalHolidays h={h} onSaved={(d) => setH((p) => ({ ...p, holidays: d.holidays, holiday_today: d.holiday_today }))} />
      <div className="flex items-center justify-between gap-3">
        <span className="text-[length:calc(var(--fs,1)*0.6875rem)] text-muted-foreground">{h.updated_at ? `Saved ${new Date(h.updated_at).toLocaleString('en-GB')}${h.updated_by_name ? ` by ${h.updated_by_name}` : ''}` : 'Not saved yet'}</span>
        <button type="button" onClick={save} disabled={busy} className="inline-flex h-10 items-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-60" data-testid="tagws-hours-save">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save hours
        </button>
      </div>
    </div>
  );
}
